import { useEffect, useMemo, useRef, useState } from "react";
import { M3_NAV } from "../data/nav";
import { portfolio } from "../data/portfolio";

interface Props {
  open: boolean;
  onClose: () => void;
}

interface Entry {
  kind: string;
  title: string;
  sub?: string;
  // Extra free-text (blurbs, details, summaries, abstracts) searched but not
  // shown — lets the search match on content, not just titles.
  body?: string;
  icon: string;
  target: string;
  external?: boolean;
}

export function SearchDialog({ open, onClose }: Props) {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (open) {
      setQ("");
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const entries: Entry[] = useMemo(() => {
    const { engineering: eng, reading, identity } = portfolio;
    return [
      // Top-level navigation sections.
      ...M3_NAV.map((n) => ({
        kind: "Section",
        title: n.label,
        icon: n.icon,
        target: "#" + n.id,
      })),
      // Experience — role, org, location, bullets and tags all searchable.
      ...portfolio.experience.map((x) => ({
        kind: "Experience",
        title: x.role,
        sub: `${x.org} · ${x.dates}`,
        body: [x.location, ...x.bullets, ...(x.tags ?? [])].join(" "),
        icon: "work_history",
        target: "#experience",
      })),
      // Projects.
      ...portfolio.projects.map((p) => ({
        kind: "Project",
        title: p.name,
        sub: p.kicker,
        body: [p.blurb, p.details.join(" "), p.stack.join(" ")].join(" "),
        icon: "deployed_code",
        target: "#projects",
      })),
      // Engineering — stack groups, vocabulary, principles, system design.
      ...eng.stack.map((g) => ({
        kind: "Stack",
        title: g.group,
        sub: "Engineering",
        body: [...g.reach, ...g.regular, ...g.ambient].join(" "),
        icon: "memory",
        target: "#engineering",
      })),
      ...eng.vocab.map((v) => ({
        kind: "Concept",
        title: v.term,
        sub: v.note,
        body: `${v.term} ${v.note} ${v.level}`,
        icon: "data_object",
        target: "#engineering",
      })),
      ...eng.principles.map((pr) => ({
        kind: "Principle",
        title: pr.title,
        sub: "Engineering",
        body: pr.body,
        icon: "rule",
        target: "#engineering",
      })),
      {
        kind: "System design",
        title: eng.systemSketch.title,
        sub: eng.systemSketch.subtitle,
        body: [...eng.systemSketch.notes, ...eng.latency.rows.map((r) => r.stage)].join(" "),
        icon: "schema",
        target: "#engineering",
      },
      // Publications.
      ...portfolio.publications.map((p) => ({
        kind: "Paper",
        title: p.title,
        sub: p.venue,
        body: [p.summary, p.authors, p.note ?? ""].join(" "),
        icon: "menu_book",
        target: "#publications",
      })),
      // Talks.
      ...portfolio.talks.map((t) => ({
        kind: "Talk",
        title: t.title,
        sub: t.venue,
        body: [t.note ?? "", t.where ?? ""].join(" "),
        icon: "mic",
        target: "#talks",
      })),
      // Awards.
      ...portfolio.awards.map((a) => ({
        kind: "Award",
        title: a.title,
        sub: a.year,
        body: a.note,
        icon: "trophy",
        target: "#awards",
      })),
      // Writing — static posts plus the always-shown extra (LinkedIn, etc.) posts.
      ...[...portfolio.extraPosts, ...portfolio.posts].map((p) => ({
        kind: "Post",
        title: p.title,
        sub: p.tag,
        body: p.kicker,
        icon: "edit_note",
        target: p.href || "#writing",
        external: !!p.href,
      })),
      // About — bio, journey milestones, now, toolkit, life outside code.
      {
        kind: "About",
        title: "About — " + identity.name,
        sub: identity.role,
        body: [identity.tagline, ...portfolio.about].join(" "),
        icon: "person",
        target: "#about",
      },
      ...portfolio.journey.map((j) => ({
        kind: "Milestone",
        title: j.title,
        sub: j.year,
        body: j.note,
        icon: "timeline",
        target: "#about",
      })),
      {
        kind: "Now",
        title: "What I'm doing now",
        body: portfolio.now.join(" "),
        icon: "bolt",
        target: "#about",
      },
      {
        kind: "Toolkit",
        title: "Tools I love",
        body: portfolio.loves.map((l) => l.name).join(" "),
        icon: "favorite",
        target: "#about",
      },
      {
        kind: "Beyond code",
        title: "Outside the terminal",
        body: portfolio.outside.map((o) => `${o.label} ${o.value}`).join(" "),
        icon: "interests",
        target: "#about",
      },
      // Reading — current reads, influences, listening.
      ...reading.nowReading.map((b) => ({
        kind: "Reading",
        title: b.title,
        sub: b.author,
        body: b.note ?? "",
        icon: "auto_stories",
        target: "#reading",
      })),
      ...reading.influences.map((b) => ({
        kind: "Influence",
        title: b.title,
        sub: b.author,
        icon: "menu_book",
        target: "#reading",
      })),
      {
        kind: "Listening",
        title: "Podcasts I follow",
        body: reading.listening.join(" "),
        icon: "podcasts",
        target: "#reading",
      },
      // Contact.
      {
        kind: "Contact",
        title: "Get in touch",
        sub: identity.email,
        body: [identity.role, identity.location, identity.tagline].join(" "),
        icon: "mail",
        target: "#contact",
      },
    ];
  }, []);

  const filtered = useMemo(() => {
    const f = q.trim().toLowerCase();
    if (!f) return entries.slice(0, 10);
    // Rank matches: title hits beat subtitle hits beat body/kind hits, and an
    // exact prefix on the title floats to the very top.
    const scored: { e: Entry; score: number }[] = [];
    for (const e of entries) {
      const title = e.title.toLowerCase();
      const sub = e.sub?.toLowerCase() ?? "";
      const body = e.body?.toLowerCase() ?? "";
      const kind = e.kind.toLowerCase();
      let score: number;
      if (title.startsWith(f)) score = 0;
      else if (title.includes(f)) score = 1;
      else if (sub.includes(f)) score = 2;
      else if (kind.includes(f)) score = 3;
      else if (body.includes(f)) score = 4;
      else continue;
      scored.push({ e, score });
    }
    return scored
      .sort((a, b) => a.score - b.score)
      .slice(0, 12)
      .map((s) => s.e);
  }, [q, entries]);

  // Reset the highlighted row whenever the result set changes.
  useEffect(() => {
    setActive(0);
  }, [q]);

  // Keep the highlighted row scrolled into view as the user arrows through.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [active]);

  if (!open) return null;

  const go = (e: Entry) => {
    onClose();
    if (e.external) {
      window.open(e.target, "_blank", "noopener,noreferrer");
      return;
    }
    setTimeout(() => {
      const el = document.querySelector(e.target);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  return (
    <div
      role="dialog"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "color-mix(in srgb, #000 50%, transparent)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "12vh",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 640,
          maxWidth: "92vw",
          borderRadius: "var(--md-sys-shape-xl)",
          background: "var(--md-sys-color-surface-container-high)",
          boxShadow: "var(--md-sys-elevation-5)",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "8px 16px", borderBottom: "1px solid var(--md-sys-color-outline-variant)" }}>
          <div className="md-searchbar" style={{ background: "transparent", height: 56, padding: 0 }}>
            <span className="material-symbols-rounded">search</span>
            <input
              ref={inputRef}
              className="md-searchbar__input md-body-l"
              placeholder="Search sections, projects, posts…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setActive((a) => Math.min(a + 1, filtered.length - 1));
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setActive((a) => Math.max(a - 1, 0));
                } else if (e.key === "Enter") {
                  e.preventDefault();
                  if (filtered[active]) go(filtered[active]);
                }
              }}
            />
            <kbd
              style={{
                fontFamily: "var(--md-sys-typescale-mono)",
                fontSize: 11,
                padding: "2px 8px",
                borderRadius: 4,
                background: "var(--md-sys-color-surface-container)",
                color: "var(--md-sys-color-on-surface-variant)",
              }}
            >
              esc
            </kbd>
          </div>
        </div>
        <ul ref={listRef} className="md-list" style={{ maxHeight: "50vh", overflow: "auto", padding: "8px 0" }}>
          {filtered.map((e, i) => (
            <li key={i}>
              <button
                type="button"
                data-idx={i}
                aria-selected={i === active}
                onClick={() => go(e)}
                onMouseMove={() => setActive(i)}
                className="md-list-item"
                style={{
                  width: "100%",
                  background:
                    i === active ? "var(--md-sys-color-surface-container-highest)" : "transparent",
                  border: 0,
                  textAlign: "left",
                }}
              >
                <span className="md-list-item__leading md-list-item__leading--tonal">
                  <span className="material-symbols-rounded icon-20">{e.icon}</span>
                </span>
                <span className="md-list-item__body">
                  <div className="md-label-s dim">{e.kind}</div>
                  <div className="md-title-m">{e.title}</div>
                  {e.sub && <div className="md-body-s dim md-list-item__support">{e.sub}</div>}
                </span>
                <span className="md-list-item__trailing">
                  <span className="material-symbols-rounded icon-20">arrow_outward</span>
                </span>
              </button>
            </li>
          ))}
          {filtered.length === 0 && (
            <li style={{ padding: "32px 24px", textAlign: "center" }} className="md-body-m dim">
              No matches for &ldquo;{q}&rdquo;.
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}
